/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import Measure from 'react-measure';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import PropTypes from 'util/PropTypes';
import BadgeTooltip from 'components/badges/BadgeTooltip';
import SingleVisDnDHandler from 'util/SingleVisDnDHandler';
import Empty from 'components/empty/Empty';
import DisplayTooltipInfo from 'components/display-tooltipinfo/DisplayTooltipInfo';

import './exploreVisWidget.scss';
/**
* Encapsulates plumbing for rendering widgets (ie visualizations)
* Manages handling visPlaceholder & widget layout
* Also, 'glides' legacy widget layout into a react view (uses ref prop)
* Each visualization can have a header section, which can be optionally filled e.g. for compare, we
* show stats table
*/

const FOOTER_HEIGHT = 17;

@observer
export class ExploreVisWidget extends Component {
	@observable activateDropTarget = false;
	constructor(props) {
		super(props);
		this.layoutOwner = props.layoutOwner;
		this.domNode = null;
		this._widgetDropTargetList = [];
		this.contentRect = {
			width: 0,
			height: 0
		};
		this._disposeEvents = [];
		this.popoverParent = React.createRef();
	}

	setBadgesInfo(badgesInfo){
		this.badgesInfo = badgesInfo;
	}

	render() {
		void(this.activateDropTarget);
		return this.getRenderMarkup();
	}

	/**
	 * 
	 * @param {Object} caption - an object containing the rows to be displayed in the popover
	 *  @returns {Array} - formatting the captions into columns for convenience in displaying
	 */
	_formatCaption(caption) {
		const category = [];
		const quantity = [];
		Object.keys(caption).forEach( (key) => {
			category.push(caption[key][0]);
			quantity.push(caption[key][1]);
		});
		return [category, quantity];
	}
	/**
	 * check if the compare line is enabled. The compare line is only disabled when the flag is set to false specifically.
	 * @returns {Boolean} true if enabled
	 */
	getIsNewCompareLineEnabled(){
		return !this.props.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('explore', 'newCompareLine', 'false');
	}

	/**
	 * Creates a popover with the formatted display info
	 * Popover is hidden when there are no matching points
	 * @param {String} id - of the Vis Model where the popover will be displayed
	 * @returns {String} - returns block to display the popover or none to hide it
	 */
	_createPopover(id){
		let formattedCaption;
		let crossHairInfo;
		let displayPopOverClass = 'popoverHidden';
		const caption = this.props.getCrossHairMatchingPoints &&
			this.props.getCrossHairMatchingPoints();
		if(caption && caption[id] && caption[id].length > 0){
			displayPopOverClass = 'popoverShown';
			formattedCaption = this._formatCaption(caption[id]);
			crossHairInfo = <DisplayTooltipInfo crossHairInfo={formattedCaption}/>;
			const totalItems = caption[id].length;
			this.popoverParent && this.popoverParent.current &&
				this.props.renderPopOver(crossHairInfo, this.popoverParent.current, totalItems,
					{ showFooter: true, footerHeight: FOOTER_HEIGHT }, this.domNode
				);
		}
		return displayPopOverClass;
	}

	getRenderMarkup() {
		const layoutOwner = this.props.layoutOwner;
		const model = this.props.model;
		const header = this.props.header;
		const id =  model.widgetLayout ? model.widgetLayout.id : model.placeholder.id;
		const getBadgesTooltipModel = model.widgetLayout && model.widgetLayout.getBadgesTooltipModel && model.widgetLayout.getBadgesTooltipModel();

		const displayPopOverClass = this.getIsNewCompareLineEnabled() && this._createPopover(id);

		let badgeTooltip = null;
		if(getBadgesTooltipModel){
			badgeTooltip = (<BadgeTooltip layoutOwner={layoutOwner} model={getBadgesTooltipModel}></BadgeTooltip>);
		}

		return (
			<Measure bounds={true}
				onResize={(args) => this.onResize(args)}
			>
				{({ measureRef }) =>
					<div className="layout-wrapper-container" ref={measureRef}>
						<div className="layout-wrapper" ref={ (node) => {this.domNode = node;} }>
							{this.activateDropTarget && this.getEmptyRenderMarkup()}
							{badgeTooltip}
							{header}
							<div className="widget-wrapper" ref={ (node) => { node && this._attachWidgetLayout(layoutOwner, node, id);} } />
							<div className={displayPopOverClass} ref={this.popoverParent}></div>
						</div>
					</div>
				}
			</Measure>
		);
	}

	onResize(contentRect) {
		//React Measure can send resize events when element has no size, guard against it
		//Do not resize when vis renders first time when this.contentRect.height/width is 0
		if( contentRect.bounds.height !== 0 && contentRect.bounds.width !== 0) {
			if (this.contentRect.height !== 0 && (contentRect.bounds.height !== this.contentRect.height || contentRect.bounds.width !== this.contentRect.width)) {
				this.props.layoutOwner.resizeIfVisible();
			}
			this.contentRect.width = contentRect.bounds.width;
			this.contentRect.height = contentRect.bounds.height;
		}
	}

	_isLoading() {
		let renderMarkupAvailable = false;
		if( this._childrenRenderMarkupAdded < this.model.numberOfChildLayoutsReady && this.model.numberOfChildLayoutsReady > 0) {
			this._childrenRenderMarkupAdded = this.model.numberOfChildLayoutsReady;
			renderMarkupAvailable = true;
		}
		return !renderMarkupAvailable;
	}

	_attachWidgetLayout(layoutOwner, parentNode, widgetId) {
		//For the interim, we are using legacy Widget layout view as the widget requires DOM nodes
		//to be pre-created
		if(parentNode && layoutOwner) {
			layoutOwner.getWidgetPromises(widgetId).setDomNode(parentNode);
		}
	}

	_cleanupWidgetDropTargets() {
		if(this._widgetDropTargetList) {
			this._widgetDropTargetList.forEach( (tgt) => {
				tgt.remove();
			});
		}
	}

	trackWidgetDropTarget(target) {
		this._widgetDropTargetList.push(target);
	}

	componentDidMount() {
		const options = {
			domNode: this.domNode,
			className: 'dndHover',
			dashboardApi: this.props.dashboardApi,
			replaceWithWidget: this.layoutOwner.replaceWithWidget.bind(this.layoutOwner),
			placeWidgetInCard: this.layoutOwner.placeWidgetInCard.bind(this.layoutOwner),
			widgetLayout: this.props.model.placeholder || this.props.model.widgetLayout,
			layoutModel: this.props.layoutOwner.model,
			activate: () => this.activateDropTarget = true,
			deactivate: () => {
				this.props.onDragEnd();
				this.activateDropTarget = false;
			}
		};
		this.props.dashboardApi.getDashboardSvc('.DndManager').then(
			(dndManager) => {
				if (this.domNode) {
					const removeDropTarget = dndManager.addDropTarget(
						this.domNode,
						new SingleVisDnDHandler(options)
					);
					this.trackWidgetDropTarget( removeDropTarget );
				}
			}
		);
		this._disposeEvents.push(
			this.props.dashboardApi.on('dashboard:show', () => this._resetContentRect())
		);
	}

	_resetContentRect() {
		this.contentRect = {
			width: 0,
			height: 0
		};
	}

	componentWillUnmount() {
		this._cleanupWidgetDropTargets();
		this._disposeEvents.forEach(dispose => dispose && dispose.remove());
		this._disposeEvents = [];
	}

	getEmptyRenderMarkup() {
		return <div className={'explore-empty-widget-layout-dnd'}>
			<Empty/>
		</div>;
	}
}

ExploreVisWidget.propTypes = {
	model: PropTypes.object,
	layoutOwner: PropTypes.object,
	header: PropTypes.object,
	dashboardApi: PropTypes.object,
	onDragEnd: PropTypes.func,
	getCrossHairMatchingPoints: PropTypes.func,
	renderPopOver: PropTypes.func
};



// WEBPACK FOOTER //
// ./src/js/layout/explorecard/ExploreVisWidget.js